import { PaymentDocument } from "../types";

interface PaymentSummaryProps {
  selectedDocuments: PaymentDocument[];
  paying: boolean;
  onPay: () => void;
  feedback?: {
    type: "success" | "error" | "info";
    message: string;
  } | null;
}

function formatCLP(amount: number): string {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(Math.round(amount));
}

const feedbackStyles: Record<"success" | "error" | "info", string> = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-700",
  error: "bg-red-50 border-red-200 text-red-700",
  info: "bg-sky-50 border-sky-200 text-sky-700",
};

export default function PaymentSummary({ selectedDocuments, paying, onPay, feedback }: PaymentSummaryProps) {
  const total = selectedDocuments.reduce((sum, doc) => sum + doc.amount, 0);

  return (
    <div className="bg-white rounded-2xl border border-sky-100 overflow-hidden sticky top-24">
      <div className="px-6 py-4 border-b border-sky-50">
        <h3 className="text-base font-bold text-slate-800" style={{ fontFamily: "'Poppins', sans-serif" }}>
          Resumen de pago
        </h3>
        <p className="text-xs text-slate-400 mt-0.5">
          {selectedDocuments.length} documento{selectedDocuments.length !== 1 ? "s" : ""} seleccionado
          {selectedDocuments.length !== 1 ? "s" : ""}
        </p>
      </div>

      <div className="px-6 py-5">
        {feedback && (
          <div className={`mb-4 rounded-xl border px-3 py-2 text-xs ${feedbackStyles[feedback.type]}`}>
            {feedback.message}
          </div>
        )}

        {selectedDocuments.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 mx-auto mb-3">
              <i className="ri-file-list-3-line text-xl"></i>
            </div>
            <p className="text-sm text-slate-400">Selecciona documentos para ver el resumen</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2 mb-4">
              {selectedDocuments.map((doc) => (
                <div key={doc.id} className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{doc.description}</p>
                    <p className="text-xs text-slate-400">{doc.period}</p>
                  </div>
                  <p className="text-xs font-semibold text-slate-700 shrink-0">{formatCLP(doc.amount)}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 my-4"></div>

            <div className="flex items-center justify-between mb-5">
              <span className="text-base font-bold text-slate-800">Total a pagar</span>
              <span className="text-xl font-bold text-sky-600">{formatCLP(total)}</span>
            </div>
          </>
        )}

        <button
          onClick={onPay}
          disabled={selectedDocuments.length === 0 || paying}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {paying ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>Redirigiendo a Transbank...
            </>
          ) : (
            <>
              <i className="ri-bank-card-line"></i>Pagar con Transbank
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="flex items-center gap-1 text-slate-400">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-sm"></i>
            </div>
            <span className="text-xs">Pago seguro</span>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-lock-line text-sm"></i>
            </div>
            <span className="text-xs">SSL cifrado</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 text-center mb-2">Medios de pago aceptados</p>
          <div className="flex items-center justify-center gap-3 text-slate-400">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-visa-line text-2xl"></i>
            </div>
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-mastercard-line text-2xl"></i>
            </div>
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-bank-card-2-line text-2xl"></i>
            </div>
          </div>
          <p className="text-xs text-slate-400 text-center mt-1">Débito y crédito</p>
        </div>
      </div>
    </div>
  );
}
